import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  NotFound,
  Notification,
  Page,
  Tab,
  TabContent,
  Tabs,
  TabsHeader,
} from 'shared/components';
import { displayValue, hasPermission, isFeatureEnabled } from 'shared/helpers';
import { getCustomerAccount } from '../actions';
import CustomerAccountDetails from './components/CustomerAccountDetails';
import CustomersTab from './components/CustomersTab';
import LicenseManagersTab from './components/LicenseManagersTab';
import OrdersTab from './components/OrdersTab';
import LicensesTab from './components/LicensesTab';
import UserGroupsTab from './components/UserGroupsTab';
import LicenseUsersTab from './components/LicenseUsersTab';
import DevicesTab from './components/DevicesTab';
import EditCustomerAccountModal from './EditCustomerAccountModal';
import './styles.scss';

const CustomerAccount = () => {
  const { t } = useTranslation();
  const { accountId } = useParams();
  const customerAccounts = useSelector((state) =>
    get(state, 'account.customerAccounts', []),
  );
  const accountFromStore = useMemo(
    () =>
      customerAccounts.find(
        (item) => String(get(item, 'id')) === String(accountId),
      ),
    [customerAccounts, accountId],
  );
  const [account, setAccount] = useState(accountFromStore || null);
  const [isLoading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const lastFetchRef = useRef({ accountId: null, retryToken: 0 });

  useEffect(() => {
    if (accountFromStore) {
      setAccount(accountFromStore);
    }
  }, [accountFromStore]);

  useEffect(() => {
    if (
      lastFetchRef.current.accountId === accountId &&
      lastFetchRef.current.retryToken === retryToken
    ) {
      return;
    }
    lastFetchRef.current = { accountId, retryToken };

    let didCancel = false;
    setLoading(true);
    setNotFound(false);
    setHasError(false);

    getCustomerAccount(accountId)
      .then((res) => {
        if (didCancel) {
          return;
        }
        setAccount(get(res, 'data', accountFromStore || null));
        setLoading(false);
      })
      .catch((error) => {
        if (didCancel) {
          return;
        }
        const status = get(error, 'response.status');
        if (status === 404) {
          setNotFound(true);
        } else {
          setHasError(true);
          Notification(
            'error',
            t('Error occurred'),
            t('There was an error while getting customer account details'),
          );
        }
        setAccount(accountFromStore || null);
        setLoading(false);
      });

    return () => {
      didCancel = true;
    };
  }, [accountId, retryToken, t, accountFromStore]);

  const handleRetry = () => setRetryToken((token) => token + 1);

  const handleEditSuccess = () => {
    setRetryToken((token) => token + 1);
  };

  const breadcrumb = (
    <NavLink to="/customer-accounts">{t('Customer accounts')}</NavLink>
  );
  const canUseUserGroups = isFeatureEnabled('usage:user-groups');
  const canManageManagers = hasPermission('manage_managers');

  if (isLoading && !account) {
    return (
      <Page title={t('Customer account')} breadcrumb={breadcrumb}>
        <ContentLoader text={t('Getting customer account details')} />
      </Page>
    );
  }

  if ((notFound || hasError) && !account) {
    return (
      <Page title={t('Customer account')} breadcrumb={breadcrumb}>
        {notFound ? (
          <NotFound />
        ) : (
          <div className="CustomerAccount-error">
            <p>{t('Customer account details could not be loaded.')}</p>
            <Button theme="info" size="md" onClick={handleRetry}>
              {t('Retry')}
            </Button>
          </div>
        )}
      </Page>
    );
  }

  return (
    <Page
      title={displayValue(get(account, 'name'), t('Customer account'))}
      breadcrumb={breadcrumb}
    >
      <div className="CustomerAccount">
        <div className="list-header">
          <div>
            {hasPermission('manage_customer_accounts') && (
              <Button
                theme="info"
                size="md"
                onClick={() => setEditModalOpen(true)}
              >
                {t('Edit account')}
              </Button>
            )}
            {hasError && (
              <Button theme="warning" size="md" onClick={handleRetry}>
                {t('Retry')}
              </Button>
            )}
          </div>
        </div>
        <CustomerAccountDetails account={account} />
        <div className="CustomerAccount-tabs">
          <Tabs>
            <TabsHeader>
              <Tab>{t('Customers')}</Tab>
              {canManageManagers && <Tab>{t('License managers')}</Tab>}
              <Tab>{t('Orders')}</Tab>
              <Tab>{t('Licenses')}</Tab>
              {canUseUserGroups && <Tab>{t('User groups')}</Tab>}
              <Tab>{t('License users')}</Tab>
              <Tab>{t('Devices')}</Tab>
            </TabsHeader>
            <TabContent>
              <CustomersTab accountId={accountId} />
            </TabContent>
            {canManageManagers && (
              <TabContent>
                <LicenseManagersTab accountId={accountId} />
              </TabContent>
            )}
            <TabContent>
              <OrdersTab accountId={accountId} />
            </TabContent>
            <TabContent>
              <LicensesTab accountId={accountId} />
            </TabContent>
            {canUseUserGroups && (
              <TabContent>
                <UserGroupsTab accountId={accountId} />
              </TabContent>
            )}
            <TabContent>
              <LicenseUsersTab accountId={accountId} />
            </TabContent>
            <TabContent>
              <DevicesTab accountId={accountId} />
            </TabContent>
          </Tabs>
        </div>
        {isEditModalOpen && (
          <EditCustomerAccountModal
            closeCb={() => setEditModalOpen(false)}
            customerAccount={account}
            confirmCb={handleEditSuccess}
          />
        )}
      </div>
    </Page>
  );
};

export default CustomerAccount;
